import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { supabase } from '../lib/supabase';
import { Actividad } from '../types';
import { AddActivityForm } from './AddActivityForm';
import { PasswordModal } from './PasswordModal';
import { ConfigModal } from './ConfigModal';
import { Plus, Calendar, Filter } from 'lucide-react';

interface MapViewProps {
  selectedYear: string;
  onYearChange: (year: string) => void;
  availableYears: string[];
}

export const MapView: React.FC<MapViewProps> = ({
  selectedYear,
  onYearChange,
  availableYears
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [heatmap, setHeatmap] = useState<google.maps.visualization.HeatmapLayer | null>(null);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  // API key de Google Maps configurada directamente
  const GOOGLE_MAPS_API_KEY = 'AIzaSyCb9g6Z-GBDTfJYl0J5CnEzEku2bZoJLsE';

  useEffect(() => {
    initMap();
  }, []);

  const initMap = async () => {
    const loader = new Loader({
      apiKey: GOOGLE_MAPS_API_KEY,
        version: 'weekly',
        libraries: ['visualization'],
      });

      try {
        await loader.load();
        
        if (mapRef.current) {
          const googleMap = new google.maps.Map(mapRef.current, {
            zoom: 14,
            center: { lat: 13.4353089, lng: -87.4523914 }, // Ubicación por defecto
            mapTypeId: 'roadmap',
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels',
                stylers: [{ visibility: 'off' }],
              },
            ],
          });

          // Agregar listener para clicks en el mapa
          googleMap.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              setSelectedLocation({
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
              });
              setShowPasswordModal(true);
            }
          });

          setMap(googleMap);
        }
      } catch (error) {
        console.error('Error loading Google Maps:', error);
        alert('Error al cargar Google Maps. Por favor, recarga la página.');
      }
  };

  useEffect(() => {
    loadActividades();
  }, []);

  useEffect(() => {
    if (map && getFilteredActividades().length > 0) {
      updateHeatmap();
      addMarkers();
    }
  }, [map, actividades, selectedYear]);


  const loadActividades = async () => {
    try {
      const { data, error } = await supabase
        .from('mapa_actividades')
        .select(`
          *,
          grupo:mapa_grupos(*)
        `);

      if (error) {
        console.error('Error loading activities:', error);
      } else {
        setActividades(data || []);
      }
    } catch (error) {
      console.error('Network error loading activities:', error);
    }
  };

  const getFilteredActividades = () => {
    if (!selectedYear) return actividades;
    return actividades.filter(actividad => 
      new Date(actividad.fecha).getFullYear().toString() === selectedYear
    );
  };

  const updateHeatmap = () => {
    if (!map) return;

    // Remover mapa de calor anterior
    if (heatmap) {
      heatmap.setMap(null);
    }

    const filteredActividades = getFilteredActividades();
    if (filteredActividades.length === 0) return;

    // Crear nuevos puntos para el mapa de calor con peso basado en alcance
    const heatmapData = filteredActividades.map(actividad => ({
      location: new google.maps.LatLng(actividad.latitud, actividad.longitud),
      weight: Math.max(1, (actividad.alcance || 50) / 50) // Normalizar el peso
    }));

    // Calcular el radio máximo basado en los alcances
    const maxAlcance = Math.max(...filteredActividades.map(a => a.alcance || 50));
    const radius = Math.min(Math.max(20, maxAlcance / 20), 100); // Radio entre 20 y 100

    const newHeatmap = new google.maps.visualization.HeatmapLayer({
      data: heatmapData,
      map: map,
      gradient: [
        'rgba(0, 255, 255, 0)',
        'rgba(0, 255, 255, 1)',
        'rgba(0, 191, 255, 1)',
        'rgba(0, 127, 255, 1)',
        'rgba(0, 63, 255, 1)',
        'rgba(0, 0, 255, 1)',
        'rgba(0, 0, 223, 1)',
        'rgba(0, 0, 191, 1)',
        'rgba(0, 0, 159, 1)',
        'rgba(0, 0, 127, 1)',
        'rgba(63, 0, 91, 1)',
        'rgba(127, 0, 63, 1)',
        'rgba(191, 0, 31, 1)',
        'rgba(255, 0, 0, 1)'
      ],
      radius: radius,
      opacity: 0.7
    });

    setHeatmap(newHeatmap);
  };

  const addMarkers = () => {
    if (!map) return;

    // Limpiar marcadores anteriores
    // (En una implementación más robusta, guardaríamos referencias a los marcadores)
    
    const filteredActividades = getFilteredActividades();
    filteredActividades.forEach(actividad => {
      // Calcular el tamaño del marcador basado en el alcance
      const markerSize = Math.max(6, Math.min(20, (actividad.alcance || 50) / 150));
      
      const marker = new google.maps.Marker({
        position: { lat: actividad.latitud, lng: actividad.longitud },
        map: map,
        title: actividad.nombre,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: markerSize,
          fillColor: actividad.grupo?.color || '#3B82F6',
          fillOpacity: 0.8,
          strokeColor: '#FFFFFF',
          strokeWeight: 2,
        },
      });

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="p-2">
            <h3 class="font-bold text-lg">${actividad.nombre}</h3>
            <p class="text-sm text-gray-600">Grupo: ${actividad.grupo?.nombre || 'Sin grupo'}</p>
            <p class="text-sm text-gray-600">Fecha: ${new Date(actividad.fecha).toLocaleDateString()}</p>
            <p class="text-sm text-gray-600">Alcance: ${actividad.alcance || 50}</p>
          </div>
        `,
      });

      marker.addListener('click', () => {
        infoWindow.open(map, marker);
      });
    });
  };

  const handleActivityAdded = () => {
    loadActividades();
    setShowAddForm(false);
    setSelectedLocation(null);
  };

  const handlePasswordSuccess = () => {
    setShowPasswordModal(false);
    setShowAddForm(true);
  };

  const handleClosePasswordModal = () => {
    setShowPasswordModal(false);
    setSelectedLocation(null);
  };

  return (
    <div className="relative h-full">
      <div ref={mapRef} className="w-full h-full" />
      
      {/* Botón flotante para agregar actividad */}
      <button
        onClick={() => setShowPasswordModal(true)}
        className="fixed bottom-6 right-6 bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-lg transition-all duration-200 hover:scale-110"
      >
        <Plus className="h-6 w-6" />
      </button>

      {/* Modal de contraseña */}
      {showPasswordModal && (
        <PasswordModal
          onClose={handleClosePasswordModal}
          onSuccess={handlePasswordSuccess}
        />
      )}

      {/* Formulario modal */}
      {showAddForm && (
        <AddActivityForm
          initialCoords={selectedLocation}
          onClose={() => {
            setShowAddForm(false);
            setSelectedLocation(null);
          }}
          onActivityAdded={handleActivityAdded}
        />
      )}

      {/* Leyenda del mapa de calor actualizada */}
      <div className="absolute bottom-6 left-4 bg-white p-3 rounded-lg shadow-lg max-w-xs">
        <h3 className="font-bold text-sm mb-2">Mapa de Calor</h3>
        <div className="flex items-center space-x-2 text-xs mb-2">
          <div className="w-4 h-4 bg-gradient-to-r from-cyan-400 to-red-600 rounded"></div>
          <span>Intensidad por alcance</span>
        </div>
        <div className="text-xs text-gray-600 space-y-1">
          <p>• Azul: Alcance bajo (1-500)</p>
          <p>• Verde: Alcance medio (500-1500)</p>
          <p>• Rojo: Alcance alto (1500-3000)</p>
        </div>
      </div>
    </div>
  );
};